// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import { InvoiceModel, InvoiceSettingsModel } from "@insite/client-framework/Types/ApiModels";
import InvoiceDetailPageTypeLink from "@insite/content-library/Components/InvoiceDetailPageTypeLink";
import { StyledButton } from "@insite/mobius/Clickable";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { EMPTY_VALUE } from "@keller/XDL/constants";
import { color, xScreen } from "@keller/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    invoice: InvoiceModel;
    session: Session;
    invoiceSettings: InvoiceSettingsModel;
    extendedStyles?: InvoiceSummaryCardStyles;
}

type Props = OwnProps;

export interface InvoiceSummaryCardStyles {
    dataCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
}

export const invoiceSummaryCardStyles: InvoiceSummaryCardStyles = {
    shipToCell: {
        css: css`
            max-width: 200px;
            white-space: pre-wrap;
            ${xScreen("lte", "md")} {
                white-space: nowrap;
                max-width: initial;
            }
        `,
    },
    dataCell: {
        css: css`
            & > ${StyledButton}, & > ${StyledButton} > ${TypographyStyle} {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 24px;
                font-weight: normal;
                color: ${color("text.main")};
                &:hover {
                    color: ${color("common.ui1")};
                }
            }
        `,
    },
};

const InvoiceSummaryCard: FC<Props> = ({ invoice, session, invoiceSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(invoiceSummaryCardStyles, extendedStyles));

    const data = {
        id: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        date: invoice.invoiceDate
            ? getLocalizedDateTime({
                  dateTime: new Date(invoice.invoiceDate),
                  language: session.language,
                  options: {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                  },
              })
            : "",
        dueDate: invoice.dueDate
            ? getLocalizedDateTime({
                  dateTime: new Date(invoice.dueDate),
                  language: session.language,
                  options: {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                  },
              })
            : "",
        shipTo: `${invoice.stCompanyName} ${invoice.btAddress1} ${invoice.btAddress2} ${invoice.shipToCity} ${invoice.shipToState}`,
        total: invoice.invoiceTotalDisplay,
    };

    // XNG-Changes: change structure to Table
    return (
        <>
            <DataTableCell {...styles.dataCell}>{data.date}</DataTableCell>
            <DataTableCell {...styles.dataCell}>
                <InvoiceDetailPageTypeLink
                    title={data.invoiceNumber || EMPTY_VALUE}
                    invoiceNumber={invoice.invoiceNumber}
                />
            </DataTableCell>
            <DataTableCell {...styles.shipToCell}>{data.shipTo || EMPTY_VALUE}</DataTableCell>
            <DataTableCell {...styles.dataCell}>{data.dueDate || EMPTY_VALUE}</DataTableCell>
            <DataTableCell {...styles.dataCell}>{data.total || EMPTY_VALUE}</DataTableCell>
        </>
    );
};

export default InvoiceSummaryCard;
